<template>
	<ModalDialog :visible="visible" @close="handleModalClose" :show-close-button="false">
		<template #header>
			<h5 class="modal-title">{{ title }}</h5>
		</template>

		<template #body>
			<p>{{ message }}</p>

			<div v-if="type === 'expiration'" class="countdown-container">
				<div class="countdown-label">Time remaining:</div>
				<div class="countdown-timer" :class="timerClass">
					{{ formattedTime }}
				</div>
				<div class="countdown-bar">
					<div
						class="countdown-progress"
						:style="{ width: progressPercent + '%' }"
						:class="progressClass"
					></div>
				</div>
			</div>
		</template>

		<template #footer>
			<button
				@click="handleExtend"
				class="btn btn-primary"
				:disabled="extending"
			>
				<span v-if="extending">Extending...</span>
				<span v-else>Extend Session</span>
			</button>
			<button @click="handleLogout" class="btn btn-secondary">
				Logout Now
			</button>
		</template>
	</ModalDialog>
</template>

<script>
import { sessionMonitor, authMutations } from '@/auth'
import ModalDialog from '@/views/shared/ModalDialog.vue'

export default {
	name: 'SessionWarning',
	components: { ModalDialog },

	data() {
		return {
			visible: false,
			type: 'expiration',
			message: '',
			timeRemaining: 0,
			initialTimeRemaining: 0,
			action: null,
			extending: false,
			countdownInterval: null
		}
	},

	computed: {
		title() {
			return this.type === 'idle' ? 'Inactive Session' : 'Session Expiring'
		},

		formattedTime() {
			const totalSeconds = Math.max(0, Math.floor(this.timeRemaining / 1000))
			const minutes = Math.floor(totalSeconds / 60)
			const seconds = totalSeconds % 60
			return `${minutes}:${seconds.toString().padStart(2, '0')}`
		},

		timerClass() {
			if (this.timeRemaining < 60000) return 'text-danger'
			if (this.timeRemaining < 180000) return 'text-warning'
			return ''
		},

		progressPercent() {
			if (!this.initialTimeRemaining) return 0
			return Math.max(0, Math.min(100, (this.timeRemaining / this.initialTimeRemaining) * 100))
		},

		progressClass() {
			if (this.progressPercent < 20) return 'bg-danger'
			if (this.progressPercent < 50) return 'bg-warning'
			return 'bg-primary'
		}
	},

	mounted() {
		window.addEventListener('session-warning', this.handleWarning)
		window.addEventListener('session-expired', this.handleExpired)
		window.addEventListener('session-refreshed', this.handleRefreshed)
	},

	beforeUnmount() {
		this.stopCountdown()
		window.removeEventListener('session-warning', this.handleWarning)
		window.removeEventListener('session-expired', this.handleExpired)
		window.removeEventListener('session-refreshed', this.handleRefreshed)
	},

	methods: {
		handleWarning(event) {
			const { type, message, timeRemaining, action } = event.detail
			this.type = type
			this.message = message
			this.timeRemaining = timeRemaining
			this.initialTimeRemaining = timeRemaining
			this.action = action
			this.visible = true
			this.extending = false

			if (type === 'expiration') {
				this.startCountdown()
			}
		},

		handleExpired() {
			this.visible = false
			this.stopCountdown()
		},

		handleRefreshed() {
			this.visible = false
			this.extending = false
			this.stopCountdown()
		},

		handleModalClose() {
			// Only allow closing idle warnings; reopen expiration warnings
			if (this.type === 'expiration' && this.timeRemaining > 0) {
				this.$nextTick(() => { this.visible = true })
			} else {
				this.visible = false
			}
		},

		startCountdown() {
			this.stopCountdown()
			this.countdownInterval = setInterval(() => {
				this.timeRemaining -= 1000
				if (this.timeRemaining <= 0) {
					this.visible = false
					this.stopCountdown()
				}
			}, 1000)
		},

		stopCountdown() {
			if (this.countdownInterval) {
				clearInterval(this.countdownInterval)
				this.countdownInterval = null
			}
		},

		async handleExtend() {
			this.extending = true
			try {
				if (this.action) {
					await this.action()
				} else {
					await sessionMonitor.refreshSession()
				}
			} catch (e) {
				this.extending = false
			}
		},

		handleLogout() {
			this.visible = false
			this.stopCountdown()
			authMutations.reset()
			this.$router.push('/login')
		}
	}
}
</script>

<style scoped>
.countdown-container {
	text-align: center;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 0.25rem;
}

.countdown-label {
	font-size: 0.875rem;
	color: #6c757d;
	margin-bottom: 0.5rem;
}

.countdown-timer {
	font-size: 2.5rem;
	font-weight: bold;
	font-family: monospace;
	margin-bottom: 0.5rem;
}

.countdown-bar {
	height: 8px;
	background: #e9ecef;
	border-radius: 4px;
	overflow: hidden;
}

.countdown-progress {
	height: 100%;
	transition: width 1s linear;
}
</style>
